import type { RegexPattern } from '../types';

/**
 * @group Constants
 * @summary <blockquote>Regex patterns for string manipulation operations.</blockquote>
 * 
 * Patterns used for common string transformations and replacements.
 */
export const STRING_MANIPULATION_PATTERNS = {
  /**
   * Matches newline characters for replacement
   * Example: Used to replace newlines with spaces in commit messages
   */
  NEWLINE: {
    regex: /\n/g,
    description: 'Matches newline characters for replacement',
    examples: ['line1\nline2', 'text\nmore'],
    counterExamples: ['line1 line2', 'text more'],
  } as RegexPattern,

  /**
   * Matches whitespace characters (spaces, tabs, newlines)
   * Example: Used to normalize whitespace
   */
  WHITESPACE: {
    regex: /\s+/g,
    description: 'Matches whitespace characters (spaces, tabs, newlines)',
    examples: ['text  more', 'text\tmore', 'text\nmore'],
    counterExamples: ['textmore', 'text-more'],
  } as RegexPattern,

  /**
   * Matches leading whitespace
   * Example: Used to trim leading spaces
   */
  LEADING_WHITESPACE: {
    regex: /^\s+/,
    description: 'Matches leading whitespace',
    examples: ['  text', '\ttext', '\n  text'],
    counterExamples: ['text', 'text  ', '  text  '],
  } as RegexPattern,

  /**
   * Matches trailing whitespace
   * Example: Used to trim trailing spaces
   */
  TRAILING_WHITESPACE: {
    regex: /\s+$/,
    description: 'Matches trailing whitespace',
    examples: ['text  ', 'text\t', 'text\n  '],
    counterExamples: ['text', '  text', '  text  '],
  } as RegexPattern,

  /**
   * Matches kebab-case, underscore, or space segment delimiters for splitting
   * Example: "my-step_name" → ["my", "step", "name"] when used with split()
   */
  KEBAB_UNDERSCORE_SEGMENT_DELIMITER: {
    regex: /[-_\s]+/g,
    description: 'Matches segment delimiters (hyphen, underscore, whitespace) for splitting kebab-case or snake_case strings',
    examples: ['my-step', 'my_step', 'my step', 'my-step_name'],
    counterExamples: ['myStep', 'mystep'],
  } as RegexPattern,
} as const;
